import { useEffect } from "react";
import Button from "../Button/Button";
import { Copy } from 'lucide-react';

const VERIFY_PATH = "/registration/verify";

const SuccessRegistration = ({ message, setForm }) => {
    const accountNumber = process.env.REACT_APP_BANK_ACCOUNT_NUMBER;

    const handleVerifyLink = () => {
        window.location.href = VERIFY_PATH;
    };

    const handleCopy = () => {
        navigator.clipboard.writeText(accountNumber);
    }

    useEffect(() => {
        window.scrollBy({
            top: -window.innerHeight,
            left: 0,
            behavior: "smooth",
        });
    }, []);

    return (
        <div className="px-5 mb-10 max-w-3xl mx-auto">
            <h1 className="md:text-3xl mb-5 text-xl font-bold text-center text-green-700">
                Registration Successful!
            </h1>

            <div className="text-center">
                {/* Success Message and RRN */}
                <p className="mb-6 text-gray-700">
                    Team Registered Successfully! Your Team RRN Number is{" "}
                    <br className="sm:hidden" />
                    <b className="text-primary whitespace-nowrap text-2xl ml-1">
                        {message.rrn}
                    </b>
                </p>

                {/* Fee Breakdown Block */}
                <div className="inline-block bg-white shadow-sm p-5 rounded-lg border border-gray-200 text-left mb-8 min-w-[300px] sm:min-w-[350px]">
                    <h4 className="font-bold text-gray-800 border-b pb-3 mb-3 text-lg">Fee Breakdown</h4>

                    <div className="space-y-2 text-sm text-gray-600">
                        <div className="flex justify-between">
                            <span>Registration Fee:</span>
                            <span className="font-medium text-gray-900">{message.competition_fee} PKR</span>
                        </div>

                        <div className="flex justify-between">
                            <span>Accommodation Fee:</span>
                            <span className="font-medium text-gray-900">{message.total_accommodation} PKR</span>
                        </div>

                        <div className="flex justify-between">
                            <span>Food Fee:</span>
                            <span className="font-medium text-gray-900">{message.total_food} PKR</span>
                        </div>
                    </div>

                    {/* Grand Total */}
                    <div className="flex justify-between border-t border-gray-300 mt-4 pt-3 text-lg bg-gray-50 -mx-5 -mb-5 px-5 py-3 rounded-b-lg">
                        <b className="text-red-600">Total Payable:</b>
                        <b className="text-red-600">{message.total} PKR</b>
                    </div>
                </div>
            </div>

            {/* NEXT STEPS SECTION */}
            <div className="mt-4 border-t-2 border-dashed border-logo-maroon-medium pt-8">
                <h2 className="text-2xl font-bold text-logo-black mb-6 text-center">
                    Payment Instructions & Next Steps
                </h2>

                <div className="bg-gray-50/30 border border-logo-maroon-medium/30 rounded-xl p-6">
                    <ul className="list-none space-y-6">
                        
                        {/* STEP 1 */}
                        <li className="relative pl-0">
                            <h3 className="text-lg font-bold text-logo-maroon-deep mb-2 flex items-center">
                                <span className="bg-primary text-white rounded-full w-6 h-6 flex items-center justify-center text-sm mr-2">1</span>
                                Transfer the Fee
                            </h3>
                            <p className="text-gray-700 mb-3 ml-8">
                                Please transfer the total amount to the following bank account. 
                                <span className="italic text-sm text-gray-500 block mt-1">(National & International participants)</span>
                            </p>
                            
                            {/* Bank Card */}
                            <div className="ml-8 bg-white p-4 rounded-md border border-gray-300 shadow-sm">
                                <div className="grid grid-cols-1 md:grid-cols-[140px_1fr] gap-y-2 text-sm md:text-base items-center">
                                    <span className="text-gray-500 font-medium">Bank Name:</span>
                                    <strong className="text-logo-black">{process.env.REACT_APP_BANK_NAME}</strong>

                                    <span className="text-gray-500 font-medium">Account Title:</span>
                                    <strong className="text-logo-black break-words">{process.env.REACT_APP_BANK_ACCOUNT_TITLE}</strong>

                                    <span className="text-gray-500 font-medium self-start md:self-center mt-1 md:mt-0">Account Number:</span>
                                    
                                    {/* Flex container for Number + Button */}
                                    <div className="flex items-center gap-2">
                                    <strong className="text-logo-maroon-dark font-mono text-md tracking-wide select-all">
                                        {accountNumber}
                                    </strong>

                                    <button
                                        onClick={handleCopy}
                                        className="p-1.5 hover:bg-gray-100 rounded-md transition-colors text-gray-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-300"
                                        title="Copy to clipboard"
                                        aria-label="Copy account number"
                                    >
                                        <Copy size={16} />
                                    </button>
                                    </div>

                                </div>
                            </div>
                        </li>

                        {/* STEP 2 */}
                        <li className="relative">
                            <h3 className="text-lg font-bold text-logo-maroon-deep mb-2 flex items-center">
                                <span className="bg-primary text-white rounded-full w-6 h-6 flex items-center justify-center text-sm mr-2">2</span>
                                Verify Payment
                            </h3>
                            <p className="text-gray-700 ml-8">
                                After depositing the fee, you <strong>must</strong> visit the verification link to upload your proof of payment:
                            </p>
                            <a
                                className="ml-8 mt-2 inline-block text-logo-maroon-dark underline font-semibold hover:text-logo-maroon-deep break-all transition-colors"
                                href={VERIFY_PATH}
                                target="_blank"
                                rel="noreferrer"
                            >
                                {VERIFY_PATH}
                            </a>
                        </li>
                    </ul>
                </div>

                {/* Important Notes */}
                <div className="mt-6 space-y-2 text-sm text-center">
                    <p className="text-red-600 font-medium bg-red-50 py-2 px-4 rounded inline-block">
                        <strong>Note:</strong> The registration fee is non-refundable.
                    </p>
                    <p className="text-gray-500">
                        Complete payment procedure has also been emailed to all team members.
                    </p>
                </div>
            </div>

            <div className="flex justify-center gap-6 mt-8">
                <Button type="button" text="Proceed to Verify Payment" onClick={handleVerifyLink} />
            </div>
        </div>
    );
};

export default SuccessRegistration;